import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell } from 'recharts';
import { X } from 'lucide-react';
import { METRICS, COMPARE_COLORS, OPTIONAL_DATA_METRICS, fmtNum } from './utils';
import { MetricValue } from './DriversTable';

export function DriverCompare({ drivers, team, onRemove }) {
  const [metricKey, setMetricKey] = useState('revenue');
  const metric = METRICS.find((m) => m.key === metricKey);
  const teamVal = metric.team(team);
  const data = drivers.map((d, i) => ({ name: d.driver_name, value: d[metricKey] || 0, prev: d.previous?.[metricKey] ?? 0, color: COMPARE_COLORS[i] }));

  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900 bg-white dark:bg-slate-900 p-4 space-y-4" data-testid="driver-compare-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mr-2">Şoför Kıyaslama</h3>
        {drivers.map((d, i) => (
          <span key={d.driver_key} className="inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-xs font-medium text-white" style={{ background: COMPARE_COLORS[i] }}>
            {d.driver_name}
            <button type="button" onClick={() => onRemove(d.driver_key)} className="rounded-full hover:bg-white/20 p-0.5" data-testid={`compare-remove-${d.driver_key}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>

      <div className="flex flex-wrap gap-1">
        {METRICS.map((m) => (
          <button key={m.key} type="button" onClick={() => setMetricKey(m.key)} className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${metricKey === m.key ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`} data-testid={`compare-metric-${m.key}`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="h-64" data-testid="compare-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(v, 0)} />
            <Tooltip formatter={(v, n) => [fmtNum(v, 2), n === 'prev' ? 'Önceki dönem' : metric.label]} />
            <ReferenceLine y={teamVal} stroke="#64748b" strokeDasharray="4 4" label={{ value: `Ekip ⌀ ${fmtNum(teamVal, 1)}`, position: 'insideTopRight', fontSize: 10, fill: '#64748b' }} />
            <Bar dataKey="prev" fill="#cbd5e1" radius={[4, 4, 0, 0]} name="prev" />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} name="value">
              {data.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-slate-400 -mt-2">Gri çubuk: önceki dönem · Kesikli çizgi: ekip ortalaması</p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="compare-table">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left py-1.5 pr-3">Metrik</th>
              {drivers.map((d, i) => <th key={d.driver_key} className="text-right py-1.5 px-2" style={{ color: COMPARE_COLORS[i] }}>{d.driver_name}</th>)}
              <th className="text-right py-1.5 pl-2">Ekip ⌀</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => (
              <tr key={m.key} className="border-t border-slate-100 dark:border-slate-800">
                <td className="py-1.5 pr-3 text-slate-600 dark:text-slate-300">{m.label}</td>
                {drivers.map((d) => (
                  <td key={d.driver_key} className="py-1.5 px-2 text-right tabular-nums">
                    {OPTIONAL_DATA_METRICS.includes(m.key) && !d[m.key] ? <span className="text-slate-300">—</span> : (
                      <>
                        <div className="font-medium text-slate-800 dark:text-slate-100"><MetricValue metric={m} value={d[m.key]} /></div>
                        <div className="text-[10px] text-slate-400">önceki: {d.previous ? <MetricValue metric={m} value={d.previous[m.key]} /> : '—'}</div>
                      </>
                    )}
                  </td>
                ))}
                <td className="py-1.5 pl-2 text-right tabular-nums text-slate-500"><MetricValue metric={m} value={m.team(team)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
